import { ComponentRef, createNgModule, Injectable, Injector, Type, ViewContainerRef } from '@angular/core';
import { ErrorHandlerService } from '@y42/error-handling';

export interface LazyLoadComponentDef<C> {
  module: undefined | (() => Promise<Type<unknown>>); // explicitely set module to undefined to load a standalone component
  component: () => Promise<Type<C>>;
}

/**
 * Service to lazy-load single-module-Angular-components and automatically render them.
 */
@Injectable()
export class LazyLoadComponentService<Component extends object, Properties extends object = Partial<Component>> {
  constructor(
    private readonly viewContainerRef: ViewContainerRef,
    private readonly injector: Injector,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private _rendering = false;
  private _componentRef: ComponentRef<Component> | null = null;

  get rendering() {
    return this._rendering;
  }

  get componentRef() {
    return this._componentRef;
  }

  async render(
    componentDef: LazyLoadComponentDef<Component>,
    properties?: Properties,
    injector = this.injector,
    viewContainerRef = this.viewContainerRef,
  ): Promise<ComponentRef<Component> | null> {
    // Since the render function is async,
    // we need to prevent multiple calls.
    if (this._rendering) {
      return this._componentRef;
    }

    // Lazy-load the component, render it, and (optionally) set properties
    this._rendering = true;
    viewContainerRef.clear();

    try {
      const ngModule = componentDef.module ? await componentDef.module() : undefined;
      const ngModuleRef = ngModule ? createNgModule(ngModule, injector) : undefined;
      const component = await componentDef.component();
      const componentRef = viewContainerRef.createComponent(component, { ngModuleRef, injector });
      this._componentRef = componentRef;

      if (properties) {
        this.setProperties(properties);
      }

      return componentRef;
    } catch (error) {
      this.errorHandlerService.handleError(error);
      return null;
    } finally {
      this._rendering = false;
    }
  }

  setProperties(properties: Properties): void {
    const componentRef = this._componentRef;
    if (!componentRef) {
      return;
    }

    Object.entries(properties).forEach(
      ([key, value]) => (componentRef.instance[key as keyof Component] = value as Component[keyof Component]),
    );
    componentRef.changeDetectorRef.markForCheck();
  }
}
